import type {
    HierarcicalObjectReference,
    ObjectData,
    ObjectDB,
    ObjectId,
    SceneData,
    SceneLoadFail,
    SearchOptions,
} from "@novorender/data-js-api";
import { vec3 } from "gl-matrix";

import { dataApi } from ".";

/**
 * The scene a `GET /api/scenes/{id}` answer describes, as `dataApi.loadScene` makes it of the
 * answer it fetches: for a scene config the viewer has from elsewhere, the offline capture's
 * `config` is the answer's JSON, and is left as it is.
 *
 * The object DB can't be made without the request `dataApi.loadScene` makes (its class isn't
 * exported): it takes the one `loadScene` resolves with the first time it is used, and its queries
 * fail until it has it, as they would offline anyway.
 * @param loadScene `dataApi.loadScene` for the scene.
 * @param isOnline Whether the viewer is online. Offline, the object DB isn't asked for: its queries
 *   fail at once, as the network would fail them, with no request made. Back online, the next
 *   query asks.
 */
export function sceneDataFromConfig(
    config: unknown,
    sceneId: string,
    loadScene: () => Promise<SceneData | SceneLoadFail> = () => dataApi.loadScene(sceneId),
    isOnline: () => boolean = () => navigator.onLine,
): SceneData {
    // What `dataApi.loadScene` does to the answer, step for step (@novorender/data-js-api 2.0.1).
    const scene = structuredClone(config) as Omit<SceneData, "bookmarks"> & {
        selectedObjects?: ObjectId[];
        hiddenObjects?: ObjectId[];
        cameraBookmarks?: { name: string; properties: unknown }[];
        bookmarks?: unknown[];
    };
    scene.db = new DeferredObjectDB(async () => {
        if (!isOnline()) {
            // As `fetch` fails offline: callers already take it for no answer.
            throw new TypeError("Failed to fetch: the viewer is offline");
        }
        const loaded = await loadScene();
        if ("error" in loaded) {
            throw loaded;
        }
        return loaded.db!;
    });

    const { settings, selectedObjects, hiddenObjects, cameraBookmarks } = scene;
    if (scene.objectGroups) {
        for (const group of scene.objectGroups) {
            if (!group.id && group.name !== "default" && group.name !== "defaultHidden") {
                group.id = crypto.randomUUID().replaceAll("-", "");
            }
        }
    } else {
        scene.objectGroups = [];
        if (settings) {
            const selected = {
                id: "",
                name: "default",
                ids: [] as ObjectId[],
                color: [1, 0, 0] as ArrayLike<number>,
                selected: true,
                hidden: false,
            };
            let hasSelected = false;
            const selectedColor = (settings as { selectedObjects?: { color?: ArrayLike<number> } }).selectedObjects
                ?.color;
            if (selectedColor) {
                selected.color = vec3.copy(vec3.create(), selectedColor as vec3);
                hasSelected = true;
            }
            if (selectedObjects) {
                selected.ids = selectedObjects;
                delete scene.selectedObjects;
                hasSelected = true;
            }
            if (hasSelected) {
                scene.objectGroups.push(selected as unknown as SceneData["objectGroups"][number]);
            }
            if (hiddenObjects) {
                scene.objectGroups.push({
                    id: "",
                    name: "defaultHidden",
                    ids: hiddenObjects,
                    color: [1, 0, 0],
                    selected: false,
                    hidden: true,
                } as unknown as SceneData["objectGroups"][number]);
            }
        }
    }

    const clippingPlanes = (settings as { clippingPlanes?: { bounds: { min: vec3; max: vec3 } } } | undefined)
        ?.clippingPlanes;
    if (clippingPlanes) {
        clippingPlanes.bounds = {
            min: vec3.copy(vec3.create(), clippingPlanes.bounds.min),
            max: vec3.copy(vec3.create(), clippingPlanes.bounds.max),
        };
    }
    if (cameraBookmarks) {
        scene.bookmarks = cameraBookmarks.map(({ name, properties }) => ({ name, camera: properties }));
        delete scene.cameraBookmarks;
    }
    return scene as SceneData;
}

type ObjectDBWithMetadata = ObjectDB & { loadCompleteMetadata?(id: number): Promise<ObjectData> };

/**
 * An object DB made once `load` resolves; asked for again after a failure.
 *
 * `AppObjectDB` caches metadata by patching `loadCompleteMetadata` on the DB it is given, which is
 * this one. The DB loaded calls its own, so it is pointed at this one's, patched or not.
 */
class DeferredObjectDB implements ObjectDB {
    private loaded: Promise<ObjectDBWithMetadata> | undefined;
    private innerLoadCompleteMetadata: ((id: number) => Promise<ObjectData>) | undefined;

    constructor(private readonly load: () => Promise<ObjectDB>) {}

    private db(): Promise<ObjectDBWithMetadata> {
        this.loaded ??= this.load().then(
            (db: ObjectDBWithMetadata) => {
                if (db.loadCompleteMetadata) {
                    this.innerLoadCompleteMetadata = db.loadCompleteMetadata.bind(db);
                    db.loadCompleteMetadata = (id) => this.loadCompleteMetadata(id);
                }
                return db;
            },
            (error) => {
                this.loaded = undefined;
                throw error;
            },
        );
        return this.loaded;
    }

    async loadCompleteMetadata(id: number): Promise<ObjectData> {
        const db = await this.db();
        return this.innerLoadCompleteMetadata ? this.innerLoadCompleteMetadata(id) : db.getObjectMetdata(id);
    }

    // Not the loaded DB's own `getObjectMetdata`. It calls `loadCompleteMetadata`, which `AppObjectDB`
    // has wrapped with its cache, so it would wait on the cache entry its own caller is filling.
    async getObjectMetdata(id: number): Promise<ObjectData> {
        const db = await this.db();
        return this.innerLoadCompleteMetadata ? this.innerLoadCompleteMetadata(id) : db.getObjectMetdata(id);
    }

    async *search(
        filter: SearchOptions,
        signal: AbortSignal | undefined,
    ): AsyncIterableIterator<HierarcicalObjectReference> {
        yield* (await this.db()).search(filter, signal);
    }

    async descendants(object: HierarcicalObjectReference, signal: AbortSignal | undefined): Promise<ObjectId[]> {
        return (await this.db()).descendants(object, signal);
    }
}
